// filepath: /Users/rashid/hdmovies/FLD_User_Transition/src/components/Blog/BlogList.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ApiUtil from '../../utils/apiUtils';
import Pagination01 from '../Pagination01';
import './blog-styles.css';

// Add CSS for blog hover effects
const blogStyles = `
    .blog-item:hover {
        transform: translateY(-5px);
        box-shadow: 0 15px 35px rgba(0,0,0,0.1) !important;
    }
    
    .blog-item:hover .product-img img {
        transform: scale(1.05);
    }
    
    .blog-item:hover .product-overlay {
        opacity: 1 !important;
    }
    
    .blog-item .product-title:hover {
        color: #e67929 !important;
    }
    
    .blog-item .btn:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 8px rgba(230, 121, 41, 0.3);
    }
    
    .post-list .product-wrap .product-content h3 {
        font-weight: 500 !important;
        height: 34px!important;
    }
`;

// Inject styles
if (typeof document !== 'undefined' && !document.getElementById('blog-list-styles')) {
    const style = document.createElement('style');
    style.id = 'blog-list-styles';
    style.textContent = blogStyles;
    document.head.appendChild(style);
}

const BlogList = () => {
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Pagination state
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 12,
    });
    const [blogData, setBlogData] = useState({
        blogs: [],
        total: 0,
        totalPages: 0,
        currentPage: 1
    });

    // Scroll to top when blogs update
    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });
    }, [blogData.blogs]);

    const handlePageSelect = (page) => {
        setPagination(prev => ({
            ...prev,
            page: Math.max(1, Math.min(page, blogData.totalPages || 1))
        }));
    };

    // Helper function to get the correct image source URL
    const getImageSrc = (imageUrl) => {
        // If it's already an absolute URL (S3), use it as is
        if (imageUrl && (imageUrl.startsWith('http://') || imageUrl.startsWith('https://'))) {
            return imageUrl;
        }
        // Otherwise, it's a local file path, prepend base URL
        return `https://hdmovietoday.xyz${imageUrl}`;
    };

    useEffect(() => {
        fetchBlogs();
    }, [pagination]);

    const fetchBlogs = async () => {
        try {
            setLoading(true);
            const response = await ApiUtil.SendAsync({
                url: '/blogs',
                method: 'GET',
                params: { 
                    ...pagination, 
                    status: 'published',
                    postType: 'post'  // Only fetch posts, not pages
                }
            });
            
            // Validate response structure
            if (!response.data) {
                throw new Error('No data received from API');
            }
            
            // Handle both new pagination structure and fallback for legacy structure
            if (response.data.blogs && Array.isArray(response.data.blogs)) {
                // New pagination structure - additional client-side filtering as backup
                const filteredBlogs = response.data.blogs.filter(blog => 
                    blog.postType === 'post'
                );
                setBlogData({
                    blogs: filteredBlogs,
                    total: response.data.total || 0,
                    totalPages: response.data.totalPages || 1,
                    currentPage: response.data.currentPage || 1
                });
                setBlogs(filteredBlogs);
            } else if (Array.isArray(response.data)) {
                // Legacy structure - filter and use just an array of blogs
                const filteredBlogs = response.data.filter(blog => 
                    blog.postType === 'post'
                );
                setBlogData({
                    blogs: filteredBlogs,
                    total: filteredBlogs.length,
                    totalPages: 1,
                    currentPage: 1
                });
                setBlogs(filteredBlogs);
            } else {
                throw new Error('Invalid response structure: blogs is not an array');
            }
            
            setLoading(false);
        } catch (error) {
            console.error('Error fetching blogs:', error);
            setError('Failed to load blogs');
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
                <div className="h4">Loading blogs...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
                <div className="h4 text-danger">{error}</div>
            </div>
        );
    }

    return (
        <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
            {/* Hero Section */}
            <div className="hero-section" style={{ 
                backgroundImage: 'url("/assets/images/blogs/blog-banner-compress.png")', 
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                color: 'white', 
                padding: '25px 0',
                position: 'relative'
            }}>
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(58, 57, 57, 0.7)',
                    zIndex: 1
                }}></div>
                <div className="container" style={{ position: 'relative', zIndex: 2 }}>
                    <div className="row">
                        <div className="col-12 text-center">
                            <h1 className="display-3 fw-bold mb-4"style={{ color: '#fff' }}>
                                Our Blog
                            </h1>
                            <p className="lead fs-4 mb-0" style={{ color: '#fff3e6', maxWidth: '600px', margin: '0 auto' }}>
                                Discover the latest trends, tips, and insights in flooring design and installation
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="container py-5 post-list">
                {/* Show total count when blogs are available */}
                {blogs.length > 0 && (
                    <div className="row mb-4">
                        <div className="col-12">
                            <div className="d-flex justify-content-between align-items-center">
                                <div className="showing-item">
                                    <span style={{ color: '#666', fontSize: '16px' }}>
                                        {blogData.total} Blog{blogData.total !== 1 ? 's' : ''} listed
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {blogs.length === 0 ? (
                    <div className="text-center py-5">
                        <div className="row justify-content-center">
                            <div className="col-md-6">
                                <div className="d-flex justify-content-center mb-4">
                                    <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center" 
                                         style={{ width: '100px', height: '100px', backgroundColor: '#e67929' }}>
                                        <svg className="text-white" width="50" height="50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                                        </svg>
                                    </div>
                                </div>
                                <h3 className="display-6 fw-bold text-dark mb-3">No blogs yet</h3>
                                <p className="fs-5 text-muted">Check back soon for exciting new content and flooring insights!</p>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="row g-4">
                        {blogs.map((blog, index) => {
                            // Generate excerpt if not available
                            let excerpt = blog.excerpt;
                            if (!excerpt && blog.body) {
                                // Try to extract text from Editor.js blocks
                                try {
                                    const bodyData = typeof blog.body === 'string' ? JSON.parse(blog.body) : blog.body;
                                    if (bodyData.blocks && bodyData.blocks.length > 0) {
                                        const firstTextBlock = bodyData.blocks.find(block => block.type === 'paragraph' && block.data && block.data.text);
                                        if (firstTextBlock) {
                                            excerpt = firstTextBlock.data.text.substring(0, 150) + (firstTextBlock.data.text.length > 150 ? '...' : '');
                                        }
                                    }
                                } catch (e) {
                                    console.warn('Failed to parse blog body for excerpt generation:', e);
                                }
                            }
                            
                            return (
                                <div key={blog._id} className="col-lg-3 col-md-6 col-sm-6 col-12">
                                    <article className="product-wrap blog-item bg-white shadow-sm h-100" 
                                         style={{ borderRadius: '15px', overflow: 'hidden', transition: 'all 0.3s ease' }}>
                                        {blog.featuredImage ? (
                                            <div className="product-img position-relative overflow-hidden">
                                                <img
                                                    src={getImageSrc(blog.featuredImage)}
                                                    alt={blog.title}
                                                    className="w-100"
                                                    style={{ height: '220px', objectFit: 'cover', transition: 'transform 0.3s ease' }}
                                                />
                                                <div className="product-overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                                                     style={{ background: 'rgba(0,0,0,0.1)', opacity: 0, transition: 'opacity 0.3s ease' }}>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="product-img d-flex align-items-center justify-content-center" 
                                                 style={{ height: '220px', backgroundColor: '#f8f9fa' }}>
                                                <svg className="text-muted" width="64" height="64" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                </svg>
                                            </div>
                                        )}
                                        
                                        <div className="product-content p-4">
                                            {/* Meta information */}
                                            <div className="blog-meta d-flex align-items-center justify-content-between mb-3">
                                                <div className="d-flex align-items-center">
                                                    <div className="d-flex align-items-center me-3">
                                                        <svg className="me-2" style={{ color: '#e67929' }} width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                        </svg>
                                                        <small className="text-muted fw-medium">
                                                            {new Date(blog.createdAt).toLocaleDateString('en-US', {
                                                                month: 'short',
                                                                day: 'numeric',
                                                                year: 'numeric'
                                                            })}
                                                        </small>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Title */}
                                            <h3 className="product-title h5 fw-bold mb-3" style={{ lineHeight: '1.3' }}>
                                                <Link
                                                    to={`/blog/${blog.slug}`}
                                                    className="text-dark text-decoration-none"
                                                    style={{ 
                                                        transition: 'color 0.3s ease',
                                                        cursor: 'pointer'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.target.style.color = '#e67929';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.target.style.color = '#212529';
                                                    }}
                                                >
                                                    {blog.title.length > 140 ? blog.title.substring(0, 140) + '...' : blog.title}
                                                </Link>
                                            </h3>

                                            {/* Excerpt */}
                                            <p className="text-muted mb-4" style={{ fontSize: '14px', lineHeight: '1.5' }}>
                                                {excerpt ? 
                                                    (excerpt.length > 120 ? excerpt.substring(0, 120) + '...' : excerpt) : 
                                                    'Discover what this blog post has to offer...'
                                                }
                                            </p>

                                            {/* Read more button */}
                                            <Link
                                                to={`/blog/${blog.slug}`}
                                                className="btn btn-sm fw-semibold text-white d-inline-flex align-items-center"
                                                style={{ 
                                                    backgroundColor: '#e67929', 
                                                    borderColor: '#e67929',
                                                    borderRadius: '8px',
                                                    transition: 'all 0.3s ease'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.backgroundColor = '#d66825';
                                                    e.target.style.borderColor = '#d66825';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.backgroundColor = '#e67929';
                                                    e.target.style.borderColor = '#e67929';
                                                }}
                                            >
                                                Read More
                                                <svg className="ms-2" width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                                </svg>
                                            </Link>
                                        </div>
                                    </article>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Pagination Controls */}
                {blogData.totalPages > 1 && (
                    <Pagination01
                        productLength={blogData.total}
                        onNextPage={() => handlePageSelect(pagination.page + 1)}
                        onPreviousPage={() => handlePageSelect(pagination.page - 1)}
                        currentPage={pagination.page}
                        pageCount={blogData.totalPages}
                        onPageSelect={handlePageSelect}
                    />
                )}
            </div>
        </div>
    );
};

export default BlogList;
